import { useEffect, useState } from 'react'
import { autoBackground, findBackground } from './backgrounds'
import { prefersReducedMotion } from './motion'

type Mode = 'light' | 'dark'

function initial(): Mode {
  const saved = localStorage.getItem('reptree-theme')
  if (saved === 'light' || saved === 'dark') return saved
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

/** Light-first theme with a user toggle (persisted) + dark support. */
export function useTheme() {
  const [mode, setMode] = useState<Mode>(initial)
  useEffect(() => {
    document.documentElement.classList.toggle('dark', mode === 'dark')
    localStorage.setItem('reptree-theme', mode)
  }, [mode])
  // Cross-fade the flip: every surface glides to its new color (see .theme-fade).
  const toggle = () => {
    if (!prefersReducedMotion()) {
      const root = document.documentElement
      root.classList.add('theme-fade')
      window.setTimeout(() => root.classList.remove('theme-fade'), 500)
    }
    setMode((m) => (m === 'dark' ? 'light' : 'dark'))
  }
  return { mode, toggle }
}

/** The landing has no theme — force light while mounted, restore the user's choice on leave. */
export function useForceLight() {
  useEffect(() => {
    const had = document.documentElement.classList.contains('dark')
    document.documentElement.classList.remove('dark')
    return () => {
      if (had || initial() === 'dark') document.documentElement.classList.add('dark')
    }
  }, [])
}

/**
 * Apply a business's branding at runtime — the WHOLE palette, not just the brand color.
 *
 * The chosen color's hue retints every token while each keeps its stock lightness/
 * saturation (the "relative shading" of the Call Sheet ladder): the three surface
 * tints, the strong ink tile, and — via a .dark block — properly lifted dark-mode
 * variants. Implemented as an injected <style> (inline vars on :root can't differ
 * per mode). No branding → the tag is removed and stock tokens take over.
 */
export function applyBranding(branding?: { primary_color?: string; accent_color?: string; background?: string }) {
  const ID = 'reptree-brand'
  const existing = document.getElementById(ID)
  // Clear any legacy inline overrides from earlier versions of this function.
  document.documentElement.style.removeProperty('--brand-primary')
  document.documentElement.style.removeProperty('--brand-accent')

  const hasBrand = Boolean(branding?.primary_color)
  const bgPreset = findBackground(branding?.background)
  // Nothing custom at all → stock tokens take over.
  if (!hasBrand && !bgPreset) {
    existing?.remove()
    return
  }

  const light: string[] = []
  const dark: string[] = []

  if (hasBrand) {
    const { h } = hexToHsl(branding!.primary_color!)
    const triplet = hexToRgb(branding!.primary_color!)
    const accent = branding!.accent_color ? hexToRgb(branding!.accent_color) : triplet
    // Each line = stock token's S/L with the brand hue (stock greens: leaf 49/92,
    // mint 33/86, sage 37/78; forest-strong 55/22; dark lifted 29/65 + dark tints).
    light.push(
      `--brand-primary:${triplet}`,
      `--brand-accent:${accent}`,
      `--brand-strong:${hsl(h, 55, 22)}`,
      `--tint-1:${hsl(h, 49, 92)}`,
      `--tint-2:${hsl(h, 33, 86)}`,
      `--tint-3:${hsl(h, 37, 78)}`,
      // Deadline urgency ladder (stock S/L per tier; tier 3 carries its own light type pair).
      `--u1-bg:${hsl(h, 38, 80)}`,
      `--u2-bg:${hsl(h, 42, 64)}`,
      `--u3-bg:${hsl(h, 52, 26)}`,
      `--u3-fg:240 238 230`,
      `--u3-muted:${hsl(h, 18, 82)}`,
    )
    dark.push(
      `--brand-primary:${hsl(h, 29, 65)}`,
      `--brand-accent:${hsl(h, 29, 65)}`,
      `--brand-strong:${hsl(h, 25, 38)}`,
      `--tint-1:${hsl(h, 15, 13)}`,
      `--tint-2:${hsl(h, 15, 16)}`,
      `--tint-3:${hsl(h, 16, 20)}`,
      `--u1-bg:${hsl(h, 16, 21)}`,
      `--u2-bg:${hsl(h, 22, 28)}`,
      `--u3-bg:${hsl(h, 30, 40)}`,
      `--u3-fg:240 238 230`,
      `--u3-muted:${hsl(h, 18, 82)}`,
    )
  }

  // Canvas: an UNDERSTATED paper, never the loud complement of older builds.
  // 'auto' (no explicit choice) whispers the brand's own hue into the bone;
  // an explicit preset overrides with its own low-saturation canvas.
  const brandHue = hasBrand ? hexToHsl(branding!.primary_color!).h : 0
  const canvas = bgPreset ?? { light: autoBackground(brandHue).light, dark: autoBackground(brandHue).dark }
  light.push(`--bg:${hsl(...canvas.light.bg)}`, `--bg-elev:${hsl(...canvas.light.elev)}`)
  dark.push(`--bg:${hsl(...canvas.dark.bg)}`, `--bg-elev:${hsl(...canvas.dark.elev)}`)

  const el = (existing as HTMLStyleElement | null) ?? document.createElement('style')
  el.id = ID
  el.textContent = `:root{${light.join(';')}}.dark{${dark.join(';')}}`
  if (!el.parentNode) document.head.appendChild(el)
}

function hexToRgb(hex: string): string {
  const h = hex.replace('#', '')
  const n = parseInt(h.length === 3 ? h.split('').map((c) => c + c).join('') : h, 16)
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`
}

function hexToHsl(hex: string): { h: number; s: number; l: number } {
  const [r, g, b] = hexToRgb(hex).split(' ').map((v) => Number(v) / 255) as [number, number, number]
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  const d = max - min
  if (d === 0) return { h: 0, s: 0, l: l * 100 }
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  const hh =
    max === r ? ((g - b) / d + (g < b ? 6 : 0)) : max === g ? (b - r) / d + 2 : (r - g) / d + 4
  return { h: hh * 60, s: s * 100, l: l * 100 }
}

/** hsl → "R G B" triplet (our CSS vars hold raw triplets for rgb(var(--x) / a)). */
function hsl(h: number, s: number, l: number): string {
  const S = s / 100
  const L = l / 100
  const f = (n: number) => {
    const k = (n + h / 30) % 12
    return Math.round(255 * (L - S * Math.min(L, 1 - L) * Math.max(-1, Math.min(k - 3, 9 - k, 1))))
  }
  return `${f(0)} ${f(8)} ${f(4)}`
}
